import { Button, Input, Label, MultiSelect } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { SelectedDrugRow } from "./SelectedDrugRow";
import { useEligibilitySubmissionForm } from "./useEligibilitySubmissionForm";

export interface EligibilitySubmissionFormProps {
  contact: TellescopeModelFromName<"endusers">;
  onSubmit: () => void;
  onCancel?: () => void;
}

export const EligibilitySubmissionForm = ({ contact, onSubmit, onCancel }: EligibilitySubmissionFormProps) => {
  const { t } = useTranslation("contacts");
  const {
    form,
    providers,
    currentMedications,
    previousMedications,
    selectedDrugs,
    drugBeingAdded,
    setDrugBeingAdded,
    addDrug,
    removeDrug,
    canSubmit,
    isSubmitting,
  } = useEligibilitySubmissionForm({ contact, onSubmit });

  return (
    <form className="flex-1 overflow-y-auto space-y-6 p-2" {...form.getFormProps()}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 p-1">
          <Label htmlFor="test-submission-type">{t("Test Submission Type")}</Label>
          <Select {...form.getCustomFieldProps("testSubmissionType")}>
            <Select.Trigger id="test-submission-type" className="w-full">
              <Select.Value placeholder={t("Submit real eligibility check")} />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="drugs_covered__prior_auth_required__has_copay">
                {t("Drugs Covered, Prior Auth Required, Has Copay")}
              </Select.Item>
            </Select.Content>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="provider">{t("Provider")}</Label>
          <Select {...form.getCustomFieldProps("provider")}>
            <Select.Trigger id="provider" className="w-full">
              <Select.Value placeholder={t("Select provider")} />
            </Select.Trigger>
            <Select.Content>
              {providers?.map((provider) => (
                <Select.Item key={provider.id} value={provider.id}>
                  {provider.fname} {provider.lname}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="insurance-type">{t("Insurance Type")}</Label>
          <Select {...form.getCustomFieldProps("insuranceType")}>
            <Select.Trigger id="insurance-type" className="w-full">
              <Select.Value placeholder={t("Select insurance type")} />
            </Select.Trigger>
            <Select.Content>
              {!!contact.insurance && <Select.Item value="Primary">{t("Primary")}</Select.Item>}
              {!!contact.insuranceSecondary && <Select.Item value="Secondary">{t("Secondary")}</Select.Item>}
            </Select.Content>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="diagnosis-codes">{t("Diagnosis Codes")}</Label>
          <MultiSelect
            id="diagnosis-codes"
            options={
              // Valued by index, not `diagnosis.id`, which is optional — see `toDiagnosisCodes`.
              contact.diagnoses?.map((diagnosis, i) => ({
                label: diagnosis.code ?? t("Unknown Diagnosis"),
                value: `${i}`,
              })) ?? []
            }
            {...form.getCustomFieldProps("diagnosisCodes")}
            placeholder={t("Select diagnosis codes")}
          />
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="currently-taking">{t("Currently Taking Drugs")}</Label>
          <MultiSelect
            id="currently-taking"
            options={
              currentMedications?.map((item) => ({
                label: item.title,
                value: item.id,
              })) ?? []
            }
            {...form.getCustomFieldProps("currentlyTaking")}
            placeholder={t("Select current medications")}
          />
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="previously-taken">{t("Previously Taken Drugs")}</Label>
          <MultiSelect
            id="previously-taken"
            options={
              previousMedications?.map((item) => ({
                label: item.title,
                value: item.id,
              })) ?? []
            }
            {...form.getCustomFieldProps("previouslyTaken")}
            placeholder={t("Select previous medications")}
          />
        </div>
      </div>

      <div className="space-y-4 border-t pt-6">
        <h3 className="text-lg font-medium">{t("Add Drugs to Check")}</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label
              htmlFor="drug-name"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("Drug Name")}
            </Label>
            <Input
              id="drug-name"
              placeholder={t("Enter drug name")}
              value={drugBeingAdded.name}
              onChange={(e) => setDrugBeingAdded({ ...drugBeingAdded, name: e.target.value })}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="dosage"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("Dosage")}
            </Label>
            <Input
              id="dosage"
              placeholder={t("e.g. 500mg")}
              value={drugBeingAdded.dosage}
              onChange={(e) => setDrugBeingAdded({ ...drugBeingAdded, dosage: e.target.value })}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="quantity"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("Quantity")}
            </Label>
            <div className="flex gap-2">
              <Input
                id="quantity"
                placeholder={t("e.g. 30 tablets")}
                value={drugBeingAdded.quantity}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value);
                  setDrugBeingAdded({ ...drugBeingAdded, quantity: Number.isNaN(parsed) ? 0 : parsed });
                }}
                className="h-10"
              />
              <Button type="button" onClick={() => addDrug()} variant="outline" className="h-10 whitespace-nowrap">
                {t("Add Drug")}
              </Button>
            </div>
          </div>
        </div>

        {selectedDrugs.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">{t("Selected Drugs:")}</h4>
            {selectedDrugs.map((drug) => (
              <SelectedDrugRow key={`${drug.name}-${drug.dosage}-${drug.quantity}`} drug={drug} onRemove={removeDrug} />
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4 border-t gap-2">
        {!!onCancel && (
          <Button onClick={onCancel} className="w-full md:w-auto" variant="outline">
            {t("Cancel")}
          </Button>
        )}
        <Button type="submit" className="w-full md:w-auto" disabled={!canSubmit || isSubmitting}>
          {t("Submit Eligibility Check")}
        </Button>
      </div>
    </form>
  );
};
